/** OS family icons for the Devices fleet column and detail Overview tile. */
import React from "react";
import { Box, Tooltip } from "@mui/material";

export type DeviceOsFamily = "windows" | "macos" | "linux" | "ios" | "android";

const OS_LABELS: Record<DeviceOsFamily, string> = {
  windows: "Windows",
  macos: "macOS",
  linux: "Linux",
  ios: "iOS",
  android: "Android",
};

/**
 * Resolve os_family from free-text OS / platform fields when the inventory row
 * did not already stamp `os_family` (e.g. device detail metadata-only path).
 */
export function resolveDeviceOsFamilyFromHints(hints: {
  osFamily?: string | null;
  operatingSystem?: string | null;
  platform?: string | null;
  osVersion?: string | null;
}): DeviceOsFamily | "" {
  const stamped = String(hints.osFamily || "")
    .trim()
    .toLowerCase();
  if (stamped === "windows" || stamped === "macos" || stamped === "linux" || stamped === "ios" || stamped === "android") {
    return stamped;
  }

  const text = [hints.operatingSystem, hints.platform, hints.osVersion]
    .map((part) => String(part || "").trim().toLowerCase())
    .filter(Boolean)
    .join(" ");
  if (!text) return "";

  if (
    /\b(iphone|ipad|ipod|ios|ipados)\b/.test(text) ||
    (text.includes("apple") && /\b(mobile|phone|pad)\b/.test(text))
  ) {
    return "ios";
  }
  if (/\b(android|pixel|galaxy)\b/.test(text)) return "android";
  if (/\b(mac\s*os|macos|os\s*x|darwin)\b/.test(text) || text.includes("mac os x")) return "macos";
  if (/\b(windows|win32|win64|microsoft windows)\b/.test(text)) return "windows";
  if (/\b(linux|ubuntu|debian|centos|rhel|red\s*hat|fedora|suse|arch)\b/.test(text)) return "linux";
  return "";
}

function OsSvg({ children }: { children: React.ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} style={{ display: "block" }} aria-hidden>
      {children}
    </svg>
  );
}

function WindowsMark() {
  return (
    <OsSvg>
      <path
        fill="currentColor"
        d="M3 5.5 10.5 4.4v7.1H3V5.5zm8.2-1.3L21 3v8.5h-9.8V4.2zM3 13.5h7.5v7.1L3 19.5v-6zm8.2 0H21V21l-9.8-1.4v-6.1z"
      />
    </OsSvg>
  );
}

function AppleMark() {
  return (
    <OsSvg>
      <path
        fill="currentColor"
        d="M16.7 12.6c0-2.1 1.7-3.1 1.8-3.2-1-1.5-2.5-1.7-3.1-1.7-1.3-.1-2.5.8-3.2.8-.7 0-1.7-.8-2.8-.7-1.4.1-2.8.8-3.5 2.1-1.5 2.6-.4 6.5 1.1 8.6.7 1 1.6 2.2 2.7 2.1 1.1-.1 1.5-.7 2.8-.7s1.7.7 2.8.7c1.2 0 1.9-1 2.6-2 .8-1.2 1.2-2.3 1.2-2.4-.03-.01-2.3-.9-2.4-3.6zM14.4 5.9c.6-.7 1-1.7.9-2.7-.9.1-1.9.6-2.5 1.3-.6.6-1.1 1.6-1 2.6 1 .1 1.9-.5 2.6-1.2z"
      />
    </OsSvg>
  );
}

function LinuxMark() {
  return (
    <OsSvg>
      <path
        fill="currentColor"
        d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.424.424 0 0 0-.11.135c-.26.268-.45.6-.663.839-.199.199-.485.267-.797.4-.313.136-.658.269-.864.68-.09.189-.136.394-.132.602 0 .199.027.4.055.536.058.399.116.728.04.97-.249.68-.28 1.145-.106 1.484.174.334.535.47.94.601.81.2 1.91.135 2.774.6.926.466 1.866.67 2.616.47.526-.116.97-.464 1.208-.946.587-.003 1.23-.269 2.26-.334.699-.058 1.574.267 2.577.2.025.134.063.198.114.333l.003.003c.391.778 1.113 1.132 1.884 1.071.771-.06 1.592-.536 2.257-1.306.631-.765 1.683-1.084 2.378-1.503.348-.199.629-.469.649-.853.023-.4-.2-.811-.714-1.376v-.097l-.003-.003c-.17-.2-.25-.535-.338-.926-.085-.401-.182-.786-.492-1.046h-.003c-.059-.054-.123-.067-.188-.135a.357.357 0 0 0-.19-.064c.431-1.278.264-2.55-.173-3.694-.533-1.41-1.465-2.638-2.175-3.483-.796-1.005-1.576-1.957-1.56-3.368.026-2.152.236-6.133-3.544-6.139zm.529 3.405h.013c.213 0 .396.062.584.198.19.135.33.332.438.533.105.259.158.459.166.724 0-.02.006-.04.006-.06v.105a.086.086 0 0 1-.004-.021l-.004-.024a1.807 1.807 0 0 1-.15.706.953.953 0 0 1-.213.335.71.71 0 0 0-.088-.042c-.104-.045-.198-.064-.284-.133a1.312 1.312 0 0 0-.22-.066c.05-.06.146-.133.183-.198.053-.128.082-.264.088-.402v-.02a1.21 1.21 0 0 0-.061-.4c-.045-.134-.101-.2-.183-.333-.084-.066-.167-.132-.267-.132h-.016c-.093 0-.176.03-.262.132a.8.8 0 0 0-.205.334 1.18 1.18 0 0 0-.09.4v.019c.002.089.008.179.02.267-.193-.067-.438-.135-.607-.202a1.635 1.635 0 0 1-.018-.2v-.02a1.772 1.772 0 0 1.15-.768c.082-.22.232-.406.43-.533a.985.985 0 0 1.594-.2zm-2.962.059h.036c.142 0 .27.048.399.135.146.129.264.288.344.465.09.199.14.4.153.667v.004c.007.134.006.2-.002.266v.08c-.03.007-.056.018-.083.024-.152.055-.274.135-.393.2.012-.09.013-.18.003-.267v-.015c-.012-.133-.04-.2-.082-.333a.613.613 0 0 0-.166-.267.248.248 0 0 0-.183-.064h-.021c-.071.006-.13.04-.186.132a.552.552 0 0 0-.12.27.944.944 0 0 0-.023.33v.015c.012.135.037.2.08.334.046.134.098.2.166.268.01.009.02.018.034.024-.07.057-.117.07-.176.136a.304.304 0 0 1-.131.068 2.62 2.62 0 0 1-.275-.402 1.772 1.772 0 0 1-.155-.667 1.759 1.759 0 0 1.08-.668 1.43 1.43 0 0 1.283-.535c.128-.133.26-.2.418-.2zm1.37 1.706c.332 0 .733.065 1.216.399.293.2.523.269 1.052.468h.003c.255.136.405.266.478.399v-.131a.571.571 0 0 1.016.47c-.123.31-.516.643-1.063.842v.002c-.268.135-.501.333-.775.465-.276.135-.588.292-1.012.267a1.139 1.139 0 0 1-.448-.067 3.566 3.566 0 0 1-.322-.198c-.195-.135-.363-.332-.612-.465v-.005h-.005c-.4-.246-.616-.512-.686-.71-.07-.268-.005-.47.193-.6.224-.135.38-.271.483-.336.104-.074.143-.102.176-.131h.002v-.003c.169-.202.436-.47.839-.601.139-.036.294-.065.466-.065zm2.8 2.142c.358 1.417 1.196 3.475 1.735 4.473.286.534.855 1.659 1.102 3.024.156-.005.33.018.513.064.646-1.671-.546-3.467-1.089-3.966-.22-.2-.232-.335-.123-.335.59.534 1.365 1.572 1.646 2.757.13.535.16 1.104.021 1.67.067.028.135.06.205.067 1.032.534 1.413.938 1.23 1.537v-.043c-.06-.003-.12 0-.18 0h-.016c.151-.467-.182-.825-1.065-1.224-.915-.4-1.646-.336-1.77.465-.008.043-.013.066-.018.135-.068.023-.139.053-.209.064-.43.268-.662.669-.793 1.187-.13.533-.17 1.156-.205 1.869v.003c-.02.334-.17.838-.319 1.35-1.5 1.072-3.58 1.538-5.348.334a2.645 2.645 0 0 0-.402-.533 1.45 1.45 0 0 0-.275-.333c.182 0 .338-.03.465-.067a.615.615 0 0 0.314-.334c.108-.267 0-.697-.345-1.163-.345-.467-.931-.995-1.788-1.521-.63-.4-.986-.87-1.15-1.396-.165-.534-.143-1.085-.015-1.645.245-1.07.873-2.11 1.274-2.763.107-.065.037.135-.408.974-.396.751-1.14 2.497-.122 3.854a8.123 8.123 0 0 1.647-2.876c.564-1.278 1.743-3.504 1.836-5.268.048.036.217.135.289.202.218.133.38.333.59.465.21.201.477.335.876.335.039.003.075.006.11.006.412 0 .73-.134.997-.268.29-.134.52-.334.74-.4h.005c.467-.135.835-.402 1.044-.7zm2.185 8.958c.037.6.343 1.245.882 1.377.588.134 1.434-.333 1.791-.765l.211-.01c.315-.007.577.01.847.268l.003.003c.208.199.305.53.391.876.085.4.154.78.409 1.066.486.527.645.906.636 1.14l.003-.007v.018l-.003-.012c-.015.262-.185.396-.498.595-.63.401-1.746.712-2.457 1.57-.618.737-1.37 1.14-2.036 1.191-.664.053-1.237-.2-1.574-.898l-.005-.003c-.21-.4-.12-1.025.056-1.69.176-.668.428-1.344.463-1.897.037-.714.076-1.335.195-1.814.12-.465.308-.797.641-.984l.045-.022zm-10.814.049h.01c.053 0 .105.005.157.014.376.055.706.333 1.023.752l.91 1.664.003.003c.243.533.754 1.064 1.189 1.637.434.598.77 1.131.729 1.57v.006c-.057.744-.48 1.148-1.125 1.294-.645.135-1.52.002-2.395-.464-.968-.536-2.118-.469-2.857-.602-.369-.066-.61-.2-.723-.4-.11-.2-.113-.602.123-1.23v-.004l.002-.003c.117-.334.03-.752-.027-1.118-.055-.401-.083-.71.043-.94.16-.334.396-.4.69-.533.294-.135.64-.202.915-.47h.002v-.002c.256-.268.445-.601.668-.838.19-.201.38-.336.663-.336zm7.159-9.074c-.435.201-.945.535-1.488.535-.542 0-.97-.267-1.28-.466-.154-.134-.28-.268-.373-.335-.164-.134-.144-.333-.074-.333.109.016.129.134.199.2.096.066.215.2.36.333.292.2.68.467 1.167.467.485 0 1.053-.267 1.398-.466.195-.135.445-.334.648-.467.156-.136.149-.267.279-.267.128.016.034.134-.147.332a8.097 8.097 0 0 1-.69.468zm-1.082-1.583V5.64c-.006-.02.013-.042.029-.05.074-.043.18-.027.26.004.063 0 .16.067.15.135-.006.049-.085.066-.135.066-.055 0-.092-.043-.141-.068-.052-.018-.146-.008-.163-.065zm-.551 0c-.02.058-.113.049-.166.066-.047.025-.086.068-.14.068-.05 0-.13-.02-.136-.068-.01-.066.088-.133.15-.133.08-.031.184-.047.259-.005.019.009.036.03.03.05v.02h.003z"
      />
    </OsSvg>
  );
}

function AndroidMark() {
  return (
    <OsSvg>
      <path
        fill="currentColor"
        d="M18.44 5.56c-.68 1.17-1.35 2.33-2.03 3.5-.04-.02-.07-.03-.11-.04-1.82-.7-3.48-.8-4.42-.79-1.86.02-3.35.46-4.26.82-.08-.15-1.75-3.02-2.02-3.49a1.15 1.15 0 0 0-.14-.19c-.33-.36-.91-.49-1.38-.2-.48.28-.71.94-.39 1.5 1.95 3.37-.1-.22 1.95 3.36.02.03-.49.26-1.39 1.02C2.9 12.18.45 14.77 0 18.99h24c-.12-1.11-.37-2.1-.75-3.07-.74-1.91-1.84-3.29-2.74-4.18a12.1 12.1 0 0 0-2.13-1.69c.66-1.12 1.31-2.26 1.96-3.38.21-.36.19-.8-.01-1.12a1.1 1.1 0 0 0-.85-.53c-.52-.05-.94.31-1.05.54zm-.04 8.46c.39.59.32 1.33-.16 1.65-.48.32-1.19.1-1.58-.49-.39-.59-.32-1.33.16-1.65.47-.32 1.18-.11 1.58.49zM7.21 13.53c.48.32.55 1.06.16 1.65-.39.59-1.1.81-1.58.49-.48-.32-.55-1.06-.16-1.65.4-.6 1.11-.81 1.58-.49z"
      />
    </OsSvg>
  );
}

function IosMark() {
  return (
    <OsSvg>
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M8.25 2.5h7.5A2.25 2.25 0 0 1 18 4.75v14.5A2.25 2.25 0 0 1 15.75 21.5h-7.5A2.25 2.25 0 0 1 6 19.25V4.75A2.25 2.25 0 0 1 8.25 2.5zm1.5 1.35a.6.6 0 0 0 0 1.2h4.5a.6.6 0 1 0 0-1.2h-4.5zM12 18.35a.95.95 0 1 0 0 1.9.95.95 0 0 0 0-1.9z"
      />
    </OsSvg>
  );
}

export function deviceOsFamilyLabel(osFamily: string | null | undefined): string {
  const key = String(osFamily || "").trim().toLowerCase();
  return OS_LABELS[key as DeviceOsFamily] || "";
}

interface DeviceOsIconProps {
  osFamily?: string | null;
  operatingSystem?: string | null;
  size?: number;
}

const DeviceOsIcon: React.FC<DeviceOsIconProps> = ({ osFamily, operatingSystem, size = 18 }) => {
  const family = String(osFamily || "").trim().toLowerCase();
  const label = deviceOsFamilyLabel(family) || "Unknown OS";
  const title = String(operatingSystem || "").trim() || label;

  let mark: React.ReactNode = null;
  if (family === "windows") mark = <WindowsMark />;
  else if (family === "macos") mark = <AppleMark />;
  else if (family === "linux") mark = <LinuxMark />;
  else if (family === "android") mark = <AndroidMark />;
  else if (family === "ios") mark = <IosMark />;

  if (!mark) {
    return (
      <Box component="span" sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
        —
      </Box>
    );
  }

  return (
    <Tooltip title={title}>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#334155",
          width: size,
          height: size,
          "& svg": { width: size, height: size },
        }}
        aria-label={title}
      >
        {mark}
      </Box>
    </Tooltip>
  );
};

export default DeviceOsIcon;
